import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { PlannedRecurringEvent } from '../../types/plannedEvents';
import { formatTime } from '../../services/plannedEventService';

interface PlannedEventCardProps {
  event: PlannedRecurringEvent;
  onEdit: () => void;
  onDelete: () => void;
  onParticipantClick: (userId: string) => void;
  onParticipantDrop?: (eventId: string, userId: string) => void;
  onParticipantRemove?: (eventId: string, userId: string) => void;
}

const PlannedEventCard: React.FC<PlannedEventCardProps> = ({
  event,
  onEdit,
  onDelete,
  onParticipantClick,
  onParticipantDrop,
  onParticipantRemove,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Get venue color or default
  const venueColor = event.venueColor || event.eventTypeColor || '#1976d2';

  // Participant list
  const participantList = event.participants || [];
  const maxToShow = 3;
  const visibleParticipants = participantList.slice(0, maxToShow);
  const remainingCount = participantList.length - maxToShow;

  // Format time range
  const timeRange = `${formatTime(event.startTime)} - ${formatTime(
    event.endTime
  )}`;

  // Handle drag events
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const userId = e.dataTransfer.getData('userId');
    const sourceEventId = e.dataTransfer.getData('eventId');

    if (userId && onParticipantDrop) {
      // Don't add if already in this event
      if (!participantList.some((p) => p.userId === userId)) {
        onParticipantDrop(event.plannedEventId, userId);

        // Remove from source event if different
        if (sourceEventId && sourceEventId !== event.plannedEventId && onParticipantRemove) {
          onParticipantRemove(sourceEventId, userId);
        }
      }
    }
  };

  const handleParticipantDragStart = (
    e: React.DragEvent,
    userId: string
  ) => {
    e.stopPropagation();
    e.dataTransfer.setData('userId', userId);
    e.dataTransfer.setData('eventId', event.plannedEventId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <Card
      elevation={isHovered ? 8 : 2}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        borderTop: 4,
        borderTopColor: venueColor,
        position: 'relative',
        backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
        '&:hover': {
          transform: 'translateY(-4px)',
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          '&:last-child': {
            pb: 2,
          },
        }}
      >
        {/* Header with Time and Actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="primary"
            sx={{ fontSize: '0.875rem' }}
          >
            {timeRange}
          </Typography>

          <Box className="event-actions" sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Szerkesztés">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                sx={{
                  opacity: isHovered ? 1 : 0.5,
                  transition: 'opacity 0.2s',
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Törlés">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                sx={{
                  opacity: isHovered ? 1 : 0.5,
                  transition: 'opacity 0.2s',
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Event Name */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '1rem',
            fontWeight: 600,
            mb: 1,
            wordBreak: 'break-word',
          }}
          onClick={handleExpandClick}
        >
          {event.eventName}
        </Typography>

        {/* Event Type */}
        <Chip
          label={event.eventTypeDisplayName}
          size="small"
          sx={{
            backgroundColor: event.eventTypeColor,
            color: '#fff',
            fontSize: '0.75rem',
            height: '22px',
            mb: 1,
          }}
        />

        {/* Venue */}
        {event.venueName && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 1,
            }}
          >
            <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {event.venueName}
            </Typography>
          </Box>
        )}

        {/* Participant Count and Expand Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {event.currentParticipants}
              {event.maxParticipants && ` / ${event.maxParticipants}`} fő
            </Typography>
          </Box>

          {participantList.length > 0 && (
            <Tooltip title={isExpanded ? 'Bezárás' : 'Résztvevők mutatása'}>
              <IconButton
                size="small"
                onClick={handleExpandClick}
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Collapsed View - First 3 Participants */}
        {!isExpanded && participantList.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              alignItems: 'center',
            }}
          >
            {visibleParticipants.map((participant) => (
              <Chip
                key={participant.userId}
                label={participant.name}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onParticipantClick(participant.userId);
                }}
                sx={{
                  height: '24px',
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              />
            ))}
            {remainingCount > 0 && (
              <Chip
                label={`+${remainingCount}`}
                size="small"
                variant="outlined"
                sx={{
                  height: '24px',
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                }}
                onClick={handleExpandClick}
              />
            )}
          </Box>
        )}

        {/* Expanded View - All Participants with Drag-and-Drop */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 1.5 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
          >
            Résztvevők (húzd át másik eseményre)
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            {participantList.map((participant) => (
              <Box
                key={participant.userId}
                draggable
                onDragStart={(e) =>
                  handleParticipantDragStart(e, participant.userId)
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  backgroundColor: 'background.default',
                  borderRadius: 1,
                  cursor: 'move',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <DragIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {participant.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {participant.userRoleName}
                </Typography>
                <Tooltip title="Eltávolítás">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onParticipantRemove) {
                        onParticipantRemove(event.plannedEventId, participant.userId);
                      }
                    }}
                    sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>

          {participantList.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}
            >
              Még nincs résztvevő. Húzz ide felhasználókat!
            </Typography>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default PlannedEventCard;
